import { Component, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { DatepickerDropdownComponent } from 'ngx-lib';
import { FormEditorBase, createValueAccessorProvider } from '../base/form-editor-base';

export interface DateRange {
    from?: Date | null;
    to?: Date | null;
    includeEmpty?: boolean | null;
}

@Component({
    selector: 'app-date-range-form-editor',
    templateUrl: './date-range-form-editor.component.html',
    styleUrls: ['./date-range-form-editor.component.scss'],
    providers: [createValueAccessorProvider(DateRangeFormEditorComponent)],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DatepickerDropdownComponent]
})
export class DateRangeFormEditorComponent extends FormEditorBase<DateRange> {
    // Input signals
    value = input<DateRange>();
    readonly = input<boolean>(false);
    labelText = input<string>();
    rangeDirection = input<'forward' | 'backward'>('forward');
    placeholderText = input<string>('Select Date');
    allowSelectEmptyDate = input<boolean>(false);

    // Internal signals for date range
    readonly dateFrom = signal<Date | undefined>(undefined);
    readonly dateTo = signal<Date | undefined>(undefined);
    readonly emptyDateSelected = signal<boolean | undefined>(undefined);

    constructor() {
        super();
    }

    onDateFromChange(date: Date | undefined): void {
        this.dateFrom.set(date);
        this.emitChange();
    }

    onDateToChange(date: Date | undefined): void {
        this.dateTo.set(date);
        this.emitChange();
    }

    onEmptyDateSelectedChange(isSelected: boolean): void {
        this.emptyDateSelected.set(isSelected);
        this.emitChange();
    }

    // Override writeValue to sync with internal signals
    override writeValue(value: DateRange): void {
        super.writeValue(value);
        this.dateFrom.set(value?.from || undefined);
        this.dateTo.set(value?.to || undefined);
        this.emptyDateSelected.set(value?.includeEmpty ?? undefined);
    }

    protected override async onEntityTextChange(): Promise<void> {
        // Not needed for date picker
        return;
    }

    protected override mapSelectedItemsToValue(): DateRange {
        return {
            from: this.dateFrom(),
            to: this.dateTo(),
            includeEmpty: this.emptyDateSelected()
        };
    }

    protected override mapValueToSelectedItems(): any[] {
        return [];
    }

    private emitChange(): void {
        const value = this.mapSelectedItemsToValue();
        this.onChange(value);
        this.onTouched();
        this.valueChange.set(value);
    }
}
